import _ from './translate.js'
import { getColor } from './texts.js';

const levels = ['debug', 'log', 'info', 'warn', 'error'];

class Logger {
    static _initUI() {
        if (this._container) return;

        this._container = document.createElement('div');
        this._container.className = 'logger-container';
        document.body.appendChild(this._container);
    }

    static _getLogLevel() {
        const level = window?.mscp?.settings?.loglevel || 'warn';
        return levels.includes(level) ? level : 'warn';
    }

    static _shouldShow(type) {
        const currentLevel = this._getLogLevel();
        const debugLevel = levels.indexOf(currentLevel);
        const msgLevel = levels.indexOf(type);
        return debugLevel !== -1 && msgLevel >= debugLevel;
    }

    static _showMessage(message, type = 'log') {
        // The on-screen banner is a developer debugging aid and must never
        // reach end users — technical strings like "Error loading image:
        // undefined" are alarming inside the customizer/checkout. 'error' is
        // the highest level, so _shouldShow() alone would always let errors
        // through. Render the banner only when loglevel is explicitly
        // 'debug'; console.* logging still runs unconditionally for
        // diagnostics (see the public debug/log/info/warn/error methods).
        if (this._getLogLevel() !== 'debug') return;
        if (!this._shouldShow(type)) return;

        this._initUI();

        const el = document.createElement('div');
        el.textContent = message;
        el.classList.add('logger-message', type);
        const color = getColor(`general_${type}_color`, null);
        if (color) {
            el.style.backgroundColor = color;
        }

        el.addEventListener('click', () => el.remove());
        this._container.appendChild(el);

        const currentLevel = this._getLogLevel();
        const isDebug = currentLevel === 'debug';

        setTimeout(() => {
            el.style.opacity = '0';
            setTimeout(() => el.remove(), 300);
        }, (!isDebug) ? 5000 : 15000);
    }

    static debug(...args) {
        console.debug(...args);
        this._showMessage(this.join(false, ...args), 'debug');
    }

    static log(...args) {
        console.log(...args);
        this._showMessage(this.join(false, ...args), 'log');
    }

    static info(...args) {
        console.info(...args);
        this._showMessage(this.join(false, ...args), 'info');
    }

    static warn(...args) {
        console.warn(...args);
        this._showMessage(this.join(true, ...args), 'warn');
    }

    static error(...args) {
        console.error(...args);
        this._showMessage(this.join(true, ...args), 'error');
    }

    static console(...args) {
        console.debug(...args);
    }

    static clear() {
        if (this._container) {
            // Clear all messages
            this._container.innerHTML = '';
        }
    }
    static join(translate, ...args) {
        if (!args.length) return '';

        let format = args[0];
        if (translate) {
            format = _(args[0]);
        }
        const rest = args.slice(1);
        let i = 0;

        const result = format.replace(/%[sdifoO%]/g, match => {
            if (match === '%%') return '%';
            const arg = rest[i++];

            switch (match) {
                case '%s': return String(arg);
                case '%d':
                case '%i': return parseInt(arg);
                case '%f': return parseFloat(arg);
                case '%o':
                case '%O':
                    try {
                        return JSON.stringify(arg, null, 2);
                    } catch {
                        return '[Object]';
                    }
                default: return match;
            }
        });

        // Lägg till överblivna argument
        const extra = rest.slice(i).map(arg => {
            if (arg === null) return 'null';
            if (arg === undefined) return 'undefined';
            if (typeof arg === 'object') {
                try {
                    return JSON.stringify(arg, null, 2);
                } catch {
                    return '[Object]';
                }
            }
            return String(arg);
        });

        return [result, ...extra].join(' ');
    }
}

export default Logger;